import { Fragment, useMemo } from 'react';
import { t } from '@lingui/macro';
import ArrowForwardIosIcon from '@material-ui/icons/ArrowForwardIos';
import { filter, first, isEmpty, reduce, uniqueId } from 'lodash';
import Link from '../links/Link';
import { usePlmId } from '../../utilities/PgmUtilities';
import { useCurrentPlmName } from '../../services/PlmService';

import useStyles from './BreadcrumbStyles';
import { LegacyIcon } from '../icon';

const entityReportingDataText = t`View entity reporting data`;

function buildReportingLinkBreadCrumb(breadCrumbs, classes) {
    const reportingLinkBreadCrumb = first(filter(breadCrumbs, (breadcrumb) => breadcrumb.reportingLink));

    if (!isEmpty(reportingLinkBreadCrumb)) {
        return (
            <div key={uniqueId()} className={classes.jumpToReportingContainer} title={entityReportingDataText}>
                <Link to={reportingLinkBreadCrumb.reportingLink} preventBlur>
                    <LegacyIcon icon="ipm-icons-jump-reporting.svg" />
                </Link>
            </div>
        );
    }
}

function buildBreadCrumbs(breadCrumbs, classes) {
    const theBreadCrumbs = reduce(
        filter(breadCrumbs, (breadcrumb) => !breadcrumb.reportingLink),
        (parts, breadCrumb) => {
            const partsLength = parts.length;
            const addSeparator = partsLength > 0;
            if (breadCrumb.link) {
                parts.push(
                    <Fragment key={uniqueId()}>
                        {addSeparator && <BreadCrumbSeparator />}
                        <div className={classes.linkContainer}>
                            <Link to={breadCrumb.link} preventBlur>
                                {breadCrumb.text}
                            </Link>
                        </div>
                    </Fragment>
                );
                return parts;
            }

            parts.push(
                <Fragment key={uniqueId()}>
                    {addSeparator && <BreadCrumbSeparator />}
                    <div key={uniqueId()} className={classes.truncatedCrumb} title={breadCrumb.text}>
                        {breadCrumb.text}
                    </div>
                </Fragment>
            );

            return parts;
        },
        []
    );

    const reportingLinkBreadCrumb = buildReportingLinkBreadCrumb(breadCrumbs, classes);
    if (reportingLinkBreadCrumb) {
        theBreadCrumbs.push(reportingLinkBreadCrumb);
    }

    return theBreadCrumbs;
}

export function BreadCrumbSeparator() {
    const classes = useStyles();

    return (
        <div className={classes.breadCrumbSeparator}>
            <ArrowForwardIosIcon />
        </div>
    );
}

export default function BreadCrumb({ breadCrumbs }) {
    const classes = useStyles();
    const plmId = usePlmId();
    const plmName = useCurrentPlmName();
    const breadCrumbParts = useMemo(() => {
        // Add the PLM name as the first breadcrumb if present (PGM only)
        const crumbs = plmId ? [{ text: plmName }, ...breadCrumbs] : breadCrumbs;
        return buildBreadCrumbs(crumbs, classes);
    }, [classes, plmId, plmName, breadCrumbs]);

    return <div className={classes.breadCrumbContainer}>{breadCrumbParts}</div>;
}
